import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AdminService,
  UpdateAdminProfileRequest,
  OnlineUsersResponse,
} from "@/services/admin/adminService";
import { toast } from "sonner";

/**
 * Main admin hook that provides all admin-related queries and mutations
 */
export const useAdmin = () => {
  const queryClient = useQueryClient();

  // Query: Fetch admins for mentions
  const adminsForMentionsQuery = useQuery({
    queryKey: ["adminsForMentions"],
    queryFn: () => AdminService.getAdminsForMentions(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Query: Fetch online users count with automatic polling every 30 seconds (optimized - only count)
  const onlineUsersCountQuery = useQuery<number>({
    queryKey: ["onlineUsersCount"],
    queryFn: () => AdminService.getOnlineUsersCount(),
    refetchInterval: 30000, // Poll every 30 seconds
    staleTime: 0, // Always consider data stale to ensure fresh data on refetch
  });

  // Mutation: Update admin activity
  const updateActivityMutation = useMutation({
    mutationFn: () => AdminService.updateActivity(),
    onSuccess: () => {
      // Invalidate online users count query to refresh the count
      queryClient.invalidateQueries({ queryKey: ["onlineUsersCount"] });
      queryClient.invalidateQueries({ queryKey: ["onlineUsers"] });
    },
    onError: (error: Error) => {},
  });

  // Mutation: Update admin profile
  const updateAdminProfileMutation = useMutation({
    mutationFn: (updates: UpdateAdminProfileRequest) =>
      AdminService.updateAdminProfile(updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminProfile"] });
      queryClient.invalidateQueries({ queryKey: ["adminsForMentions"] }); // Refresh admin list in case name changed
      toast.success("Profil uppdaterad");
    },
    onError: (error: Error) => {
      toast.error("Kunde inte uppdatera profil");
    },
  });

  return {
    // Queries
    adminsForMentions: {
      data: adminsForMentionsQuery.data || [],
      isLoading: adminsForMentionsQuery.isLoading,
      error: adminsForMentionsQuery.error,
      refetch: adminsForMentionsQuery.refetch,
    },
    onlineUsersCount: {
      data: onlineUsersCountQuery.data || 0,
      isLoading: onlineUsersCountQuery.isLoading,
      error: onlineUsersCountQuery.error,
      refetch: onlineUsersCountQuery.refetch,
    },
    fetchOnlineUsersPage: async (page: number, limit: number = 25) => {
      // Always fetch fresh data, ignore cache
      return await queryClient.fetchQuery<OnlineUsersResponse>({
        queryKey: ["onlineUsers", page, limit],
        queryFn: () => AdminService.getOnlineUsers(page, limit),
        staleTime: 0, // Always consider stale
      });
    },

    // Mutations
    updateActivityAsync: updateActivityMutation.mutateAsync,
    updateAdminProfileAsync: updateAdminProfileMutation.mutateAsync,

    // Mutation states
    isUpdatingActivity: updateActivityMutation.isPending,
    isUpdatingAdminProfile: updateAdminProfileMutation.isPending,

    // Mutation errors
    updateActivityError: updateActivityMutation.error?.message || null,
    updateAdminProfileError: updateAdminProfileMutation.error?.message || null,
  };
};
